import React, { useState, useEffect, forwardRef } from "react";
import { Box, TextInput, Text, Popover, Group, Autocomplete, Badge } from "@mantine/core";
import axios from "axios";
import tinycolor from "tinycolor2";

import FilterConditionSelector from "./FilterConditionSelector";

import { useDebounce } from "../../../../hooks/useDebounce";
import { conditions, GEOAPIFY_API_KEY, getLocationFormat } from "../../../../../utils/Filters";
import isSpaceThemed from "../../../../../utils/IsSpaceThemed";
import isDark from "../../../../../utils/IsDarkColor";

export default function FilterLocationInput({
  type,
  filter_id,
  section,
  filterFormat = "text",
  options,
  value,
  label,
  color,
  colors,
  selectedOptions,
  topLocations,
  isEditMode,
  onChange,
  TargetComponent,
  onClearFilter,
  onUpdateFilter
}) {
  const [opened, setOpened] = useState(false);
  const [openedSearch, setOpenedSearch] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [condition, setCondition] = useState("contains");
  const [search, setSearch] = useState("");
  const [searchData, setSearchData] = useState([]);

  const [inputValue, setInputValue] = useState(value || "");

  const locationFormat = typeof options?.format === "string" ? options.format : "city_country";
  const locationValue = getLocationFormat(value, locationFormat);
  const _value = typeof locationValue === "string" && locationValue.length > 0 ? locationValue : null;
  const _topLocations = Array.isArray(topLocations) ? topLocations : [];

  const _color = (typeof inputValue === "string" && inputValue.length > 0) || _value
    ? color || "black"
    : color || "grey";

  const border = isSpaceThemed(colors)
    ? `1px solid ${
      isDark(colors.background_color)
        ? tinycolor(colors.background_color).lighten(20).toString()
        : tinycolor(colors.background_color).darken(20).toString()
    }`
    : `1px solid #e9ecef`;
  const inputStyles = {
    border,
    backgroundColor: isSpaceThemed(colors) ? colors.background_color : "#FFFFFF",
    color: isSpaceThemed(colors) ? colors.default_text_color : "#000000",
    "&:focus": { border }
  };

  useEffect(() => {
    if (window.$dropdownFilterId && window.$dropdownFilterId === filter_id) {
      setOpened(true);
      window.$dropdownFilterId = null;
    }
  }, []);

  useEffect(() => {
    setInputValue(value ? value : Array.isArray(selectedOptions) ? (selectedOptions[0] || "") : "");
  }, [value, selectedOptions]);

  const closePopover = () => {
    setOpened(false);
    setIsEditing(false);
    window.$tiptapSidemenu = false;
    setSearch("");
    setSearchData([]);
  };

  const handleUpdate = (val, newCondition = condition) => {
    setInputValue(val);
    if (typeof onChange === "function") {
      onChange(val, newCondition);
    }
  };

  const onSearch = (val) => {
    if (val.length > 0 && typeof val === "string") {
      axios.get(`https://api.geoapify.com/v1/geocode/autocomplete`, { 
        params: {
          text: val,
          format: "json",
          type: "city",
          lang: "en",
          apiKey: GEOAPIFY_API_KEY
        }
      })
      .then((res) => {
        if (Array.isArray(res?.data?.results)) {
          setSearchData(res.data.results);
        }
      })
      .catch((error) => console.log("GEOAPIFY_ERROR:", error?.message, error?.response, error));
    } else {
      if (searchData.length > 0) setSearchData([]);
    }
  };

  const debouncedSave = useDebounce((val) => handleUpdate(val), 500);
  const debouncedSearch = useDebounce((val) => onSearch(val), 500);

  const onConditionChange = (item) => {
    setCondition(item.value);
    handleUpdate(["is_empty", "is_not_empty"].includes(item.value) ? "" : inputValue, item.value);
  };

  const renderInput = () => {
    if (isEditing) {
      return (
        <Popover
          opened={openedSearch}
          onClose={() => {
            setIsEditing(false);
            setOpenedSearch(false);
            window.$tiptapSidemenu = false;
          }}
          position="bottom"
          placement="start"
          zIndex={100000}
          spacing={0}
          width={280}
          style={{ top: -30 }}
        >
          <TextInput
            value={search}
            size="xs"
            onChange={(e) => {
              setSearch(e.currentTarget.value);
              debouncedSearch(e.currentTarget.value);
            }}
            m={8}
          />
          {searchData.map((item, key) => (
            <Group
              key={key}
              mx={5}
              p={5}
              sx={{ cursor: "pointer", borderRadius: 5, "&:hover": { backgroundColor: "#f3f3f3" } }}
              onClick={() => {
                handleUpdate(item);
                closePopover();
              }}
            >
              <Text size="sm">{getLocationFormat(item, locationFormat)}</Text>
            </Group>
          ))}
        </Popover>
      );
    } else {
      if (["is_empty", "is_not_empty"].includes(condition)) return null;
      return (
        <TextInput
          value={inputValue}
          size="xs"
          onChange={(e) => {
            setInputValue(e.currentTarget.value);
            debouncedSave(e.currentTarget.value);
          }}
          onKeyDown={(e) => e.key === "Enter" && closePopover()}
          m={8}
        />
      );
    }
  };

  const renderSearch = () => {
    const data = searchData.map((item) => {
      const newValue = getLocationFormat(item, locationFormat);
      return { value: newValue || `${item.city}, ${item.country}`, place_id: item.place_id };
    });
    return (
      <Autocomplete
        value={search}
        size="sm"
        placeholder={_value || "Search for location"}
        data={data}
        zIndex={999999}
        dropdownPosition="bottom"
        onChange={(value) => {
          setSearch(value);
          debouncedSearch(value);
        }}
        onItemSubmit={(item) => {
          const locationItem = searchData.find((l) => l.place_id === item.place_id);
          if (locationItem) {
            handleUpdate(locationItem);
          }
        }}
        styles={{ input: { ...inputStyles } }}
        onFocus={onFocus}
        onBlur={onBlur}
      />
    );
  };

  const onFocus = () => {
    window.$tiptapSidemenu = true;
  };

  const onBlur = () => {
    window.$tiptapCanFocus = true;
  };

  if (isEditMode && type !== "panel") {
    return renderSearch();
  }

  return (
    <Popover
      opened={isEditMode ? opened : false}
      onClose={isEditMode ? closePopover : undefined}
      position="bottom"
      placement="start"
      zIndex={99999}
      spacing={0}
      style={{ width: isEditing ? "100%" : undefined }}
      target={
        TargetComponent ? (
          <TargetComponent onClick={() => setOpened(!opened)} />
        ) : (
          <Box>
            {isEditing ? (
              renderInput()
            ) : (
              <Box
                py={2}
                sx={{
                  cursor: isEditMode ? "pointer" : undefined,
                  "&:hover": isEditMode && { backgroundColor: "lightgrey" },
                  borderRadius: 5,
                }}
                onClick={isEditMode ? () => {
                  window.$tiptapSidemenu = true;
                  setIsEditing(true);
                  setOpenedSearch(true);
                 } : type === "card" ? (e) => {
                  e.stopPropagation();
                  if (typeof onUpdateFilter === "function") {
                    onUpdateFilter(_value ? [_value] : "", condition);
                  }
                } : undefined}
              >
                <Text
                  color={_color}
                  size={type === "page" || section === "list" ? "md" : "sm"}
                  lineClamp={type === "card" ? 2 : undefined}
                  style={{ lineHeight: 1.3 }}
                >
                  {_value || "Empty"}
                </Text>
              </Box>
            )}
          </Box>
        )
      }
    >
      {typeof label === "string" && (
        <FilterConditionSelector
          label={label}
          condition={condition}
          conditionData={conditions[filterFormat]}
          onChangeCondition={onConditionChange}
          onClearFilter={onClearFilter}
        />
      )}
      {TargetComponent && !["is_empty", "is_not_empty"].includes(condition) && renderInput()}
      {_topLocations.map((item, index) => {
        return (
          <Group
            key={index}
            px={10}
            py={5}
            sx={{ maxWidth: 300, cursor: "pointer", borderRadius: 5, "&:hover": { backgroundColor: "#f3f3f3" } }}
            onClick={() => {
              setInputValue(item.location_name);
              debouncedSave(item.location_name);
            }}
          >
            <Text size="sm" lineClamp={1} sx={{ flex: 1 }}>{item.location_name}</Text>
            {typeof item?.usage_count === "number" && <Badge size="sm">{item.usage_count}</Badge>}
          </Group>
        );
      })}
      {_topLocations.length > 0 && <Box mb={5} />}
    </Popover>
  );
}